"use client";

import { c as _c } from "react/compiler-runtime";
import { getProfileWave, type ApiProfileWaveResponse } from "@/services/api/profile-wave-api";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
interface ProfileWaveIdentitySource {
  readonly query?: string | null | undefined;
  readonly handle?: string | null | undefined;
  readonly normalised_handle?: string | null | undefined;
  readonly primary_wallet?: string | null | undefined;
  readonly primary_address?: string | null | undefined;
  readonly id?: string | null | undefined;
}
const normalizeProfileWaveIdentity = (identity: string | null | undefined): string => identity?.trim().toLowerCase() ?? "";
const getProfileWaveQueryKey = (identity: string | null | undefined) => ["profile-wave", normalizeProfileWaveIdentity(identity)] as const;
export const getProfileWaveIdentity = (profile: ProfileWaveIdentitySource | null | undefined): string => normalizeProfileWaveIdentity(profile?.query ?? profile?.normalised_handle ?? profile?.handle ?? profile?.primary_wallet ?? profile?.primary_address ?? profile?.id ?? null);
const getProfileWaveIdentityAliases = (...profiles: readonly (ProfileWaveIdentitySource | null | undefined)[]): string[] => {
  const aliases = new Set<string>();
  for (const profile of profiles) {
    const candidates = [profile?.query, profile?.handle, profile?.normalised_handle, profile?.primary_wallet, profile?.primary_address, profile?.id];
    for (const candidate of candidates) {
      const identity = normalizeProfileWaveIdentity(candidate);
      if (identity.length > 0) {
        aliases.add(identity);
      }
    }
  }
  return [...aliases];
};
export const setProfileWaveQueryData = (queryClient: QueryClient, profiles: readonly (ProfileWaveIdentitySource | null | undefined)[], data: ApiProfileWaveResponse): void => {
  for (const identity of getProfileWaveIdentityAliases(...profiles)) {
    queryClient.setQueryData<ApiProfileWaveResponse>(getProfileWaveQueryKey(identity), data);
  }
  if (data.profile_wave_id === null) {
    return;
  }
  queryClient.setQueriesData<ApiProfileWaveResponse>({
    queryKey: ["profile-wave"]
  }, current => current?.profile_wave_id === data.profile_wave_id ? data : current);
};
export const invalidateProfileWaveQueries = async (queryClient: QueryClient, profiles: readonly (ProfileWaveIdentitySource | null | undefined)[]): Promise<void> => {
  await Promise.all(getProfileWaveIdentityAliases(...profiles).map(identity => queryClient.invalidateQueries({
    queryKey: getProfileWaveQueryKey(identity)
  })));
};
export function useProfileWave(t0) {
  const $ = _c(13);
  const {
    identity,
    initialProfileWave,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== identity) {
    t2 = normalizeProfileWaveIdentity(identity);
    $[0] = identity;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const normalizedIdentity = t2;
  let t3;
  if ($[2] !== normalizedIdentity) {
    t3 = getProfileWaveQueryKey(normalizedIdentity);
    $[2] = normalizedIdentity;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== normalizedIdentity) {
    t4 = async t5 => {
      const {
        signal
      } = t5;
      return await getProfileWave({
        identity: normalizedIdentity,
        signal
      });
    };
    $[4] = normalizedIdentity;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = enabled && normalizedIdentity.length > 0;
  let t6;
  if ($[6] !== initialProfileWave) {
    t6 = initialProfileWave === undefined ? {} : {
      initialData: initialProfileWave,
      initialDataUpdatedAt: 0
    };
    $[6] = initialProfileWave;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      staleTime: 60000,
      ...t6
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return useQuery(t7);
}